import type { LaneConfig } from 'commons';
import { isDate, isWithinInterval } from 'date-fns';
import type { CommentModel } from '../../../services/api/models/CommentModel';
import type { AnomalyLaneIntervals } from '../services/kpi-series-builder.service';
import type { TestPhaseInterval } from '../services/test-phase-mark-area-builder.service';
import { TooltipHelper } from '../TooltipHelper.class';

const LANE_SEPARATOR_DIV = '<div class="border-t border-outlineVariant my-2 pt-2"></div>';

type TooltipParam = {
  data?: unknown[];
  value?: unknown[];
  seriesId?: string;
  metaData?: Record<string, string | number>;
  [key: string]: unknown;
};

function resolveTooltipDate(raw: unknown): Date | null {
  if (raw instanceof Date) {
    return raw;
  }
  if (typeof raw === 'number' || typeof raw === 'string') {
    return new Date(raw);
  }
  return null;
}

/**
 * Finds the anomaly metadata active at the hovered timestamp across all lanes.
 */
function findActiveAnomalyMetaData(tooltipDate: Date | null, laneAnomalies?: AnomalyLaneIntervals[]): Record<string, string | number> | undefined {
  if (!tooltipDate || !laneAnomalies?.length) {
    return undefined;
  }

  for (const lane of laneAnomalies) {
    const activeInterval = lane.intervals.find((interval) => tooltipDate >= interval.startTime && tooltipDate <= interval.endTime);
    if (activeInterval?.metaData) {
      return activeInterval.metaData as Record<string, string | number>;
    }
  }

  return undefined;
}

/**
 * Combine elements to html for the tooltip
 * @param params
 * @param comments
 * @param laneConfigs
 * @param laneTestPhases - Optional test phase intervals per xAxisIndex for active phase display
 * @param laneAnomalies - Optional anomaly intervals per xAxisIndex for active anomaly metadata display
 * @returns
 */
export function formatTooltip(
  params: unknown[],
  comments: CommentModel[],
  laneConfigs?: LaneConfig[],
  laneTestPhases?: { xAxisIndex: number; intervals: TestPhaseInterval[] }[],
  laneAnomalies?: AnomalyLaneIntervals[]
): string {
  const tooltipParams = params as TooltipParam[];
  const firstParam = tooltipParams[0];
  const firstData = Array.isArray(firstParam?.data) ? firstParam.data : [];
  const firstValue = Array.isArray(firstParam?.value) ? firstParam.value : [];

  const showableComments = comments
    .filter((c) => c.showInLane)
    .filter((c) => {
      const rawDate = firstData[0];
      return (
        isDate(rawDate) &&
        isWithinInterval(rawDate, {
          start: new Date(c.startTime),
          end: new Date(c.endTime),
        })
      );
    })
    .map((c) => `<p class="max-w-96 text-wrap">${c.comment}</p>`);

  // Filter out unwanted series types
  const filteredParams = tooltipParams
    .filter((param) => !String(param.seriesId ?? '').includes('Min'))
    .filter((param) => !String(param.seriesId ?? '').includes('kpi'))
    .filter((param) => !String(param.seriesId ?? '').includes('hide'))
    .filter((param) => !String(param.seriesId ?? '').includes('Max'));

  // Resolve the current tooltip timestamp for test phase lookup
  const tooltipRawTime = firstValue[0] ?? firstData[0];
  const tooltipDate: Date | null = resolveTooltipDate(tooltipRawTime);

  // Group parameters by lane and sort so hovered lane appears first
  const laneGroups = TooltipHelper.groupParametersByLane(filteredParams);
  const hoveredLaneIndex = TooltipHelper.determineHoveredLane(filteredParams);

  const sortedLaneEntries = TooltipHelper.sortLanesByHoveredFirst(laneGroups, hoveredLaneIndex);

  // Generate sensor HTML by processing lanes in sorted order
  let sensors = '';
  sortedLaneEntries.forEach(([laneIndex, laneParams], groupIndex) => {
    // Add lane separator if this is not the first lane group
    if (groupIndex > 0) {
      sensors += LANE_SEPARATOR_DIV;
    }

    // Add lane title if lane configurations are provided
    if (laneConfigs && laneIndex < laneConfigs.length) {
      const laneLabel = laneConfigs[laneIndex]?.config?.label ?? `Lane ${laneIndex + 1}`;
      sensors += `<div class="font-semibold text-label-small mb-1">${laneLabel}</div>`;
    }

    // Add active test phase for this lane if available
    if (tooltipDate && laneTestPhases) {
      const laneIntervals = laneTestPhases.find((lt) => lt.xAxisIndex === laneIndex)?.intervals;
      const activePhase = laneIntervals?.find((interval) => tooltipDate >= interval.startTime && tooltipDate <= interval.endTime);
      if (activePhase) {
        sensors += `<div class="flex items-center gap-1 text-xs mb-1">
            <div class="w-3 h-3 rounded-sm flex-shrink-0" style="background:${activePhase.backgroundColor};border:1px solid ${activePhase.borderColor}"></div>
            <span style="color:${activePhase.labelColor};font-weight:600">${activePhase.phaseName}</span>
          </div>`;
      }
    }

    // Process parameters for this lane
    const sortedLaneParams = laneParams.toSorted((a, b) => String(b.seriesName).localeCompare(String(a.seriesName)));
    const laneHtml = sortedLaneParams.reduce((_acc, param) => {
      const colorBubble = TooltipHelper.getColorCircle(param.color);
      const name = `<span class="overflow-hidden">${param.seriesName}</span>`;
      const value = TooltipHelper.formatDataPointValue(param.data);
      const unit = `<span>${param.data[2] ?? ''}</span>`;
      const result = TooltipHelper.generateFlexContainer({
        colorBubble,
        name,
        value,
        unit,
      });

      return `${result}${_acc}`;
    }, '');

    sensors += laneHtml;
  });

  const anomalyMetaData = findActiveAnomalyMetaData(tooltipDate, laneAnomalies);
  const dateValue = firstValue[0] ?? firstData[0];
  const date =
    typeof dateValue === 'string' || typeof dateValue === 'number' || dateValue instanceof Date ? TooltipHelper.convertPossibleTimestampToLocal(dateValue) : '';
  // Add timestamp at the end
  sensors = date + LANE_SEPARATOR_DIV + sensors;

  return TooltipHelper.generateTooltipFlex({
    sensors,
    anomalies: TooltipHelper.generateKeyValueList(anomalyMetaData),
    comments: showableComments,
  });
}
